import React, { useState } from "react";
import { faBell, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MaterialTable from "material-table";
import moment from "moment";
import "moment/min/locales";
import { Button, Col, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalHeader, Row, UncontrolledDropdown } from "reactstrap";
import { tableIcons } from "../../helpers/tableIcons";
import local from "../../localization/strings";
import SMSUsage from "../admin-area/SmsUsage";
import { SiteRequiresMFA } from "../../api/auth";
import MFA from "../mfa/MFA";
import { apiSmsDistributorPackageSave } from "../../api/smsPackages";
import { toast } from "react-toastify";
import { AvField, AvForm } from "availity-reactstrap-validation";

const defaultSmsPackage = {
	segments: undefined,
	notes: undefined,
};

const DistributorSiteList = ({ sites, refreshSites, accessSite, siteSettings, mfaSettings, activateEmergencyAlarms, deactivateEmergencyAlarms }) => {
	const [mfaSiteId, setMFASiteId] = useState(undefined);
	const [smsSite, setSmsSite] = useState(undefined);
	const [showLoading, setShowLoading] = useState(false);

	async function saveSmsPackage(_e, values) {
		setShowLoading(true);

		try {
			await apiSmsDistributorPackageSave(smsSite.id, values.segmentsAdded, values.notes);
			toast.success(local.TS_Update_Successful);
		} catch (error) {
			toast.error(local.TF_SmsPackageSaveError);
		}

		// Reload packages
		setSmsSite(undefined);
		setShowLoading(false);
		refreshSites();
	}

	const columns = [
		{
			title: local.TF_Customer,
			field: "displayName",
			defaultSort: "asc",
			render: (rowData) => (
				<div className="mx-auto">
					<div className="d-flex flex-nowrap">
						{rowData.darcaHubOffline ? <FontAwesomeIcon className="danger-icon fs-1 mr-2" icon={faExclamationCircle} title={local.TF_Darca_hub_offline} /> : <FontAwesomeIcon className="danger-icon fs-1 mr-2 invisible" icon={faBell} />}
						{rowData.emergencyAlarmCountOn > 0 ? <FontAwesomeIcon className="danger-icon fs-1 mr-2" icon={faBell} title={local.TS_Emergency_Alarms_Enabled} /> : <FontAwesomeIcon className="danger-icon fs-1 mr-2 invisible" icon={faExclamationCircle} />}
						<span>{rowData.displayName}</span>
					</div>
				</div>
			),
		},
		{ title: local.TF_ProductType, field: "productType" },
		{
			title: local.TF_ContractStart,
			field: "contractStartDate",
			render: (rowData) => <div>{rowData.contractStartDate ? moment(rowData.contractStartDate).format("DD MMM YYYY") : ""}</div>,
		},
		{ title: local.TF_NumberOfUsers, field: "userCount" },
		{ title: local.TF_NumberOfTransmitters, field: "transmitterCount" },
		{ title: local.TF_NumberOfTransmitterChannels, field: "channelsWithTransmitterCount" },
		{
			title: local.TF_Data_from,
			field: "latestReadingDateTime",
			export: false,
			render: (rowData) => <div>{rowData.latestReadingDateTime ? moment(rowData.latestReadingDateTime).format("DD MMM YYYY HH:mm") : ""}</div>,
		},
		{
			title: local.TF_Data_from,
			field: "latestReadingDateTimeFormatted",
			export: true,
			cellStyle: { display: "none" },
			headerStyle: { display: "none" },
		},
		{ title: local.TF_SMS_Usage, field: "smsUsage", render: (rowData) => <SMSUsage limit={rowData.annualSmsSegmentLimit} remaining={rowData.activeSmsSegmentsRemaining} siteId={rowData.id} /> },
		{
			title: local.TS_Actions,
			render: (rowData) => (
				<>
					{SiteRequiresMFA(rowData.id) ? (
						<>
							<FontAwesomeIcon icon="lock" className="cursor-pointer" onClick={() => setMFASiteId(rowData.id)} />
						</>
					) : (
						<UncontrolledDropdown style={{ position: "static" }}>
							<DropdownToggle tag="div" className="pin-button">
								<FontAwesomeIcon icon="ellipsis-v" fixedWidth />
							</DropdownToggle>
							<DropdownMenu className="border">
								<DropdownItem onClick={() => accessSite(rowData.id)}>{local.TF_Access_Site}</DropdownItem>
								<DropdownItem onClick={() => siteSettings(rowData.id)}>{local.TF_Change_Settings}</DropdownItem>
								<DropdownItem onClick={() => mfaSettings(rowData.id)}>{local.TF_Configure_MFA}</DropdownItem>
								<DropdownItem
									disabled={rowData.hasLockedSmsPackage}
									onClick={() => {
										setSmsSite({ id: rowData.id, smsPackageLimit: rowData.annualSmsSegmentLimit && Math.round(rowData.annualSmsSegmentLimit / 2) });
									}}
								>
									{local.TF_Sms_AddPackage}
									{rowData.hasLockedSmsPackage && local.TF_Sms_Locked}
								</DropdownItem>
								{rowData.emergencyAlarmCountOff > 0 && (
									<DropdownItem onClick={() => activateEmergencyAlarms(rowData.id)}>
										{local.TS_Emergency_Alarms_Enable}
										<div className="fs--2">({local.TS_Emergency_Alarms_Warn})</div>
									</DropdownItem>
								)}
								{rowData.emergencyAlarmCountOn > 0 && <DropdownItem onClick={() => deactivateEmergencyAlarms(rowData.id)}>{local.TS_Emergency_Alarms_Disable}</DropdownItem>}
							</DropdownMenu>
						</UncontrolledDropdown>
					)}
				</>
			),
		},
	];

	return (
		<>
			<Modal isOpen={!!mfaSiteId} toggle={() => setMFASiteId(undefined)} centered backdrop="static">
				<ModalBody>
					<MFA siteId={mfaSiteId} onCancel={() => setMFASiteId(undefined)} />
				</ModalBody>
			</Modal>

			<Modal isOpen={!!smsSite} toggle={() => setSmsSite(undefined)} centered backdrop="static">
				<ModalHeader>
					<span className="float-left">{local.TF_SmsAddPackage}</span>
					<span className="float-right close-modal" style={{ position: "absolute", top: 15, right: 20 }} onClick={() => setSmsSite(undefined)}>
						X
					</span>
				</ModalHeader>
				<ModalBody>
					<AvForm model={defaultSmsPackage} onValidSubmit={async (e, values) => await saveSmsPackage(e, values)}>
						<AvField
							name="segmentsAdded"
							type="number"
							max={smsSite?.smsPackageLimit}
							label={local.TF_SmsSegments}
							validate={{
								required: { value: true, errorMessage: local.TS_Required },
								min: { value: 1, errorMessage: `${local.TF_ValidateGreaterEqual} 1` },
								max: { value: smsSite?.smsPackageLimit, errorMessage: `${local.TF_ValidateLessEqual} ${smsSite?.smsPackageLimit}` },
								step: { value: 1, errorMessage: local.TF_ValidatePositiveInteger },
							}}
						/>
						<AvField name="notes" type="textarea" rows="3" maxLength="1000" label={local.TF_Notes} />
						<Row>
							<Col>
								<Button disabled={showLoading} color="primary" className="mt-2 float-left">
									{local.TS_Save}
								</Button>
								<Button color="secondary" className="mt-2 ml-2 float-left" onClick={() => setSmsSite(undefined)}>
									{local.TS_Cancel}
								</Button>
							</Col>
						</Row>
					</AvForm>
				</ModalBody>
			</Modal>

			<div className="MuiTable text-center">
				<MaterialTable
					columns={columns}
					data={sites}
					title=""
					icons={tableIcons}
					options={{
						padding: "dense",
						sorting: true,
						exportButton: true,
						exportAllData: true,
						exportFileName: "Site List",
						paging: true,
						pageSize: 20,
						pageSizeOptions: [20, 50, 100],
						emptyRowsWhenPaging: false,
						headerStyle: {
							backgroundColor: "#fff",
							color: "#000",
						},
						rowStyle: {
							textAlign: "center",
						},
					}}
				/>
			</div>
		</>
	);
};

export default DistributorSiteList;
